import { useState, useRef, useEffect } from 'react';
import type { ChatMessage as ChatMessageType } from '../../hooks/usePolicyChat';
import { ChatMessage } from './ChatMessage';

const EXAMPLE_PROMPTS = [
  'Allow mobile app to only GET from Google and GitHub',
  'Block all DELETE requests to production',
  'Require approval for admin operations',
  'Allow read-only access to Slack channels',
];

export interface PolicyChatProps {
  messages: ChatMessageType[];
  isStreaming: boolean;
  error: string | null;
  onSend: (text: string) => void;
}

export function PolicyChat({ messages, isStreaming, error, onSend }: PolicyChatProps) {
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 120) + 'px';
    }
  }, [input]);

  function handleSend() {
    if (!input.trim() || isStreaming) return;
    onSend(input.trim());
    setInput('');
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  // Welcome state
  if (messages.length === 0) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex flex-1 flex-col items-center justify-center px-6">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-[rgba(139,92,246,0.1)]">
            <svg className="h-6 w-6 text-accent-violet" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-text-primary">Policy Builder</h2>
          <p className="mt-1 text-center text-sm text-text-secondary">
            Describe your security policy in plain language and I'll generate the rules for you.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            {EXAMPLE_PROMPTS.map((prompt) => (
              <button
                key={prompt}
                type="button"
                onClick={() => {
                  setInput(prompt);
                  textareaRef.current?.focus();
                }}
                className="rounded-full border border-border-dark bg-surface-2 px-3 py-1.5 text-xs text-text-secondary transition-colors hover:border-accent-violet hover:text-accent-violet"
              >
                {prompt}
              </button>
            ))}
          </div>
        </div>

        {/* Input area */}
        <div className="border-t border-border-dark p-4">
          {error && (
            <p className="mb-2 text-xs text-[var(--color-error)]">{error}</p>
          )}
          <div className="flex items-end gap-2">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Describe your policy..."
              rows={1}
              className="flex-1 resize-none rounded-lg border border-border-dark bg-surface-2 px-3 py-2.5 text-sm text-text-primary placeholder:text-text-tertiary focus:border-accent-violet focus:outline-none focus:ring-1 focus:ring-accent-violet"
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || isStreaming}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent-violet text-white transition-colors hover:opacity-90 disabled:opacity-50"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
              </svg>
            </button>
          </div>
          <p className="mt-1.5 text-xs text-text-tertiary">
            Press Enter to send, Shift+Enter for new line
          </p>
        </div>
      </div>
    );
  }

  // Chat state with messages
  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Messages list */}
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {messages.map((msg, i) => {
          const isLast = i === messages.length - 1;
          return (
            <ChatMessage
              key={i}
              message={msg}
              isStreaming={isLast && msg.role === 'assistant' && isStreaming}
            />
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div className="shrink-0 border-t border-border-dark p-4">
        {error && (
          <p className="mb-2 text-xs text-[var(--color-error)]">{error}</p>
        )}
        <div className="flex items-end gap-2">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask to refine the policy..."
            rows={1}
            disabled={isStreaming}
            className="flex-1 resize-none rounded-lg border border-border-dark bg-surface-2 px-3 py-2.5 text-sm text-text-primary placeholder:text-text-tertiary focus:border-accent-violet focus:outline-none focus:ring-1 focus:ring-accent-violet disabled:opacity-50"
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || isStreaming}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent-violet text-white transition-colors hover:opacity-90 disabled:opacity-50"
          >
            {isStreaming ? (
              <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : (
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
              </svg>
            )}
          </button>
        </div>
        <p className="mt-1.5 text-xs text-text-tertiary">
          Press Enter to send, Shift+Enter for new line
        </p>
      </div>
    </div>
  );
}
